/**
 * Seed merchant registry.
 *
 * This is a *starting point*, not a closed list. Unknown merchants are normalized
 * heuristically and learned into the database over time (see `merchant_aliases`
 * in the API). UAE merchants are over-represented because that is the launch
 * market, but nothing here is structurally UAE-specific — adding a market means
 * adding rows, not changing code.
 *
 * `patterns` are matched case-insensitively against the cleaned raw merchant
 * string. They are treated as regular expressions.
 */
export const SEED_MERCHANTS = [
    // ── Groceries ──────────────────────────────────────────────────────────────
    m('carrefour', 'Carrefour', 'groceries', 'supermarket', ['carrefour', 'carrefor', 'majid al futtaim hyper', '\\bmaf\\s*hyper']),
    m('lulu', 'Lulu Hypermarket', 'groceries', 'supermarket', ['lulu\\s*hyper', '\\blulu\\b', 'lulu express']),
    m('spinneys', 'Spinneys', 'groceries', 'supermarket', ['spinneys', 'spinney']),
    m('waitrose', 'Waitrose', 'groceries', 'supermarket', ['waitrose']),
    m('union_coop', 'Union Coop', 'groceries', 'supermarket', ['union\\s*coop', 'unioncoop']),
    m('choithrams', 'Choithrams', 'groceries', 'supermarket', ['choithram']),
    m('viva', 'Viva', 'groceries', 'supermarket', ['\\bviva\\s*(supermarket|market)?\\b']),
    m('almaya', 'Al Maya', 'groceries', 'supermarket', ['al\\s*maya', 'almaya']),
    m('geant', 'Géant', 'groceries', 'supermarket', ['geant', 'géant']),
    m('zoom', 'ZOOM', 'groceries', 'convenience', ['\\bzoom\\s*(store|market)?\\b']),
    m('kibsons', 'Kibsons', 'groceries', 'supermarket', ['kibsons']),
    m('kenz_hypermarket', 'Kenz Hypermarket', 'groceries', 'supermarket', ['kenz\\s*(hyper|super)?']),
    m('west_zone', 'West Zone', 'groceries', 'supermarket', ['west\\s*zone']),
    // ── Restaurants & delivery ─────────────────────────────────────────────────
    m('talabat', 'Talabat', 'restaurants', 'delivery', ['talabat']),
    m('deliveroo', 'Deliveroo', 'restaurants', 'delivery', ['deliveroo']),
    m('noon_food', 'Noon Food', 'restaurants', 'delivery', ['noon\\s*food']),
    m('careem_food', 'Careem Food', 'restaurants', 'delivery', ['careem\\s*food']),
    m('zomato', 'Zomato', 'restaurants', 'delivery', ['zomato']),
    m('mcdonalds', "McDonald's", 'restaurants', 'fast_food', ['mcdonald', "mc\\s*donald", '\\bmcd\\b']),
    m('kfc', 'KFC', 'restaurants', 'fast_food', ['\\bkfc\\b', 'kentucky fried']),
    m('burger_king', 'Burger King', 'restaurants', 'fast_food', ['burger\\s*king', '\\bbk\\s+restaurant']),
    m('subway', 'Subway', 'restaurants', 'fast_food', ['\\bsubway\\b']),
    m('shake_shack', 'Shake Shack', 'restaurants', 'fast_food', ['shake\\s*shack']),
    m('five_guys', 'Five Guys', 'restaurants', 'fast_food', ['five\\s*guys']),
    m('pizza_hut', 'Pizza Hut', 'restaurants', 'fast_food', ['pizza\\s*hut']),
    m('dominos', "Domino's Pizza", 'restaurants', 'fast_food', ['domino']),
    m('al_baik', 'Al Baik', 'restaurants', 'fast_food', ['al\\s*baik', 'albaik']),
    m('operation_falafel', 'Operation Falafel', 'restaurants', 'dine_in', ['operation\\s*falafel', '\\bop\\s*falafel']),
    m('pf_changs', "P.F. Chang's", 'restaurants', 'dine_in', ['pf\\s*chang']),
    m('cheesecake_factory', 'The Cheesecake Factory', 'restaurants', 'dine_in', ['cheesecake\\s*factory']),
    // Dessert chains seen constantly on UAE card statements.
    m('haagen_dazs', 'Häagen-Dazs', 'restaurants', 'dessert', ['haagen\\s*-?\\s*dazs']),
    m('baskin_robbins', 'Baskin Robbins', 'restaurants', 'dessert', ['baskin\\s*robbins']),
    m('cold_stone', 'Cold Stone Creamery', 'restaurants', 'dessert', ['cold\\s*stone']),
    m('bateel', 'Bateel', 'restaurants', 'dessert', ['bateel']),
    m('patchi', 'Patchi', 'restaurants', 'dessert', ['patchi', "pat'?chi"]),
    // ── Coffee ─────────────────────────────────────────────────────────────────
    m('starbucks', 'Starbucks', 'coffee', 'cafe', ['starbucks', 'star\\s*bucks']),
    m('costa', 'Costa Coffee', 'coffee', 'cafe', ['costa\\s*coffee', '\\bcosta\\b']),
    m('tim_hortons', 'Tim Hortons', 'coffee', 'cafe', ['tim\\s*horton']),
    m('caribou', 'Caribou Coffee', 'coffee', 'cafe', ['caribou']),
    m('%_arabica', '% Arabica', 'coffee', 'specialty', ['arabica\\s*coffee', '%\\s*arabica']),
    m('nero', 'Caffè Nero', 'coffee', 'cafe', ['caffe\\s*nero', 'cafe\\s*nero']),
    m('dunkin', 'Dunkin’', 'coffee', 'cafe', ['dunkin']),
    m('kaak_al_manara', 'Kaak Al Manara', 'coffee', 'cafe', ['kaak\\s*al\\s*manara']),
    // ── Transport ──────────────────────────────────────────────────────────────
    m('careem', 'Careem', 'transportation', 'taxi', ['careem(?!\\s*food)']),
    m('uber', 'Uber', 'transportation', 'taxi', ['\\buber\\b(?!\\s*eats)']),
    m('rta', 'RTA Dubai', 'transportation', 'metro', ['\\brta\\b', 'roads\\s*(&|and)\\s*transport', 'dubai\\s*metro', '\\bnol\\b']),
    m('salik', 'Salik', 'transportation', 'tolls', ['salik']),
    m('darb', 'Darb Toll', 'transportation', 'tolls', ['\\bdarb\\b']),
    m('parkin', 'Parkin', 'transportation', 'parking', ['parkin\\b', 'dubai\\s*parking', 'mawaqif']),
    m('yango', 'Yango', 'transportation', 'taxi', ['yango']),
    m('hala_taxi', 'Hala Taxi', 'transportation', 'taxi', ['hala\\s*taxi']),
    // ── Buy now, pay later ─────────────────────────────────────────────────────
    //
    // Categorised as shopping because that is what most plans are taken out for,
    // and the message never says. What matters more is that these are recognised
    // at all: recurring.ts reads the merchant key to tell an instalment plan —
    // which ends — from a subscription, which does not.
    m('tabby', 'Tabby', 'shopping', 'installments', ['\\btabby\\b']),
    m('tamara', 'Tamara', 'shopping', 'installments', ['\\btamara\\b']),
    m('postpay', 'Postpay', 'shopping', 'installments', ['\\bpostpay\\b', 'post\\s*pay\\s*(uae|technologies)']),
    m('spotii', 'Spotii', 'shopping', 'installments', ['\\bspotii\\b']),
    // ── Fuel ───────────────────────────────────────────────────────────────────
    m('enoc', 'ENOC', 'fuel', 'petrol', ['\\benoc\\b', 'emirates national oil']),
    m('adnoc', 'ADNOC', 'fuel', 'petrol', ['\\badnoc\\b', 'abu dhabi national oil']),
    m('eppco', 'EPPCO', 'fuel', 'petrol', ['\\beppco\\b']),
    m('emarat', 'Emarat', 'fuel', 'petrol', ['\\bemarat\\b']),
    // ── Shopping / e-commerce ──────────────────────────────────────────────────
    m('amazon', 'Amazon', 'shopping', 'online', ['amazon(\\.ae|\\.com|\\.co\\.uk)?', 'amzn', 'souq\\.com']),
    m('noon', 'Noon', 'shopping', 'online', ['\\bnoon\\b(?!\\s*food)', 'noon\\.com']),
    m('sharaf_dg', 'Sharaf DG', 'electronics', 'devices', ['sharaf\\s*dg']),
    m('jumbo', 'Jumbo Electronics', 'electronics', 'devices', ['jumbo\\s*electronic', '\\bjumbo\\b']),
    m('emax', 'Emax', 'electronics', 'devices', ['\\bemax\\b']),
    m('apple', 'Apple', 'electronics', 'devices', ['apple\\s*store', 'apple\\.com(?!/bill)']),
    m('ikea', 'IKEA', 'housing', 'furniture', ['\\bikea\\b']),
    m('home_centre', 'Home Centre', 'housing', 'furniture', ['home\\s*centre', 'home\\s*center']),
    m('ace', 'ACE Hardware', 'housing', 'maintenance', ['\\bace\\b\\s*(hardware)?']),
    m('daiso', 'Daiso', 'shopping', 'home', ['daiso']),
    m('namshi', 'Namshi', 'clothing', 'apparel', ['namshi']),
    m('shein', 'SHEIN', 'clothing', 'apparel', ['\\bshein\\b']),
    m('h_and_m', 'H&M', 'clothing', 'apparel', ['h\\s*&\\s*m\\b', '\\bh\\s*and\\s*m\\b', '\\bhennes\\b']),
    m('zara', 'Zara', 'clothing', 'apparel', ['\\bzara\\b']),
    m('r_and_b', 'R&B', 'clothing', 'apparel', ['\\br\\s*(?:and|&)\\s*b\\b']),
    m('centrepoint', 'Centrepoint', 'clothing', 'apparel', ['centrepoint', 'centre\\s*point']),
    m('nike', 'Nike', 'clothing', 'shoes', ['\\bnike\\b']),
    m('adidas', 'Adidas', 'clothing', 'shoes', ['adidas']),
    m('sephora', 'Sephora', 'personal_care', 'cosmetics', ['sephora']),
    m('bath_body', 'Bath & Body Works', 'personal_care', 'cosmetics', ['bath\\s*&?\\s*body']),
    // ── Bills & telecom ────────────────────────────────────────────────────────
    m('du', 'du', 'bills_utilities', 'mobile', ['\\bdu\\s*(telecom|payment|bill)', '^du$', 'emirates integrated tele']),
    // "\\be&\\b" could never match: `\\b` after "&" demands a word character, so
    // the real descriptor "e& Digital App" (star of the rebrand) always missed.
    m('etisalat', 'Etisalat', 'bills_utilities', 'mobile', ['etisalat', '\\be&(?!\\w)', 'e\\s*&\\s*uae']),
    m('dewa', 'DEWA', 'bills_utilities', 'electricity_water', ['\\bdewa\\b', 'dubai electricity']),
    m('addc', 'ADDC', 'bills_utilities', 'electricity_water', ['\\baddc\\b', 'abu dhabi distribution']),
    m('sewa', 'SEWA', 'bills_utilities', 'electricity_water', ['\\bsewa\\b']),
    m('empower', 'Empower', 'bills_utilities', 'cooling', ['empower', 'district cooling']),
    m('tabreed', 'Tabreed', 'bills_utilities', 'cooling', ['tabreed']),
    m('virgin_mobile', 'Virgin Mobile', 'bills_utilities', 'mobile', ['virgin\\s*mobile']),
    // ── Subscriptions ──────────────────────────────────────────────────────────
    m('netflix', 'Netflix', 'subscriptions', 'streaming', ['netflix'], true),
    m('spotify', 'Spotify', 'subscriptions', 'music', ['spotify'], true),
    m('youtube_premium', 'YouTube Premium', 'subscriptions', 'streaming', ['youtube\\s*premium', 'google\\s*\\*?\\s*youtube'], true),
    m('shahid', 'Shahid', 'subscriptions', 'streaming', ['shahid'], true),
    m('osn', 'OSN', 'subscriptions', 'streaming', ['\\bosn\\b'], true),
    m('anghami', 'Anghami', 'subscriptions', 'music', ['anghami'], true),
    m('disney_plus', 'Disney+', 'subscriptions', 'streaming', ['disney\\s*\\+?', 'disneyplus'], true),
    m('apple_services', 'Apple Services', 'subscriptions', 'cloud', ['apple\\.com/bill', 'itunes', 'apple\\s*services', 'icloud'], true),
    m('google_services', 'Google', 'subscriptions', 'cloud', ['google\\s*\\*', 'google\\s*(one|storage|cloud)'], true),
    // A "FACEBK *<ref>" descriptor is Meta's ad billing — the consumer app itself
    // never charges a card. Someone running no ads will correct this once, and
    // the learned-preference layer takes over from there.
    m('meta_ads', 'Meta Ads', 'business', 'advertising', ['\\bfacebk\\b', '\\bfacebook\\b', 'meta\\s*platforms']),
    m('microsoft', 'Microsoft', 'subscriptions', 'cloud', ['microsoft', 'msft\\s*\\*', 'office\\s*365'], true),
    m('adobe', 'Adobe', 'subscriptions', 'cloud', ['adobe'], true),
    m('openai', 'OpenAI', 'subscriptions', 'cloud', ['openai', 'chatgpt'], true),
    m('anthropic', 'Anthropic', 'subscriptions', 'cloud', ['anthropic', 'claude\\.ai'], true),
    m('amazon_prime', 'Amazon Prime', 'subscriptions', 'streaming', ['amazon\\s*prime', 'prime\\s*video'], true),
    // ── Health & fitness ───────────────────────────────────────────────────────
    m('life_pharmacy', 'Life Pharmacy', 'health', 'pharmacy', ['life\\s*pharmacy']),
    m('aster', 'Aster', 'health', 'clinic', ['\\baster\\b']),
    m('mediclinic', 'Mediclinic', 'health', 'clinic', ['mediclinic']),
    m('nmc', 'NMC Healthcare', 'health', 'clinic', ['\\bnmc\\b']),
    m('boots', 'Boots Pharmacy', 'health', 'pharmacy', ['\\bboots\\b']),
    m('fitness_first', 'Fitness First', 'fitness', 'gym', ['fitness\\s*first'], true),
    m('gold_gym', "Gold's Gym", 'fitness', 'gym', ["gold'?s\\s*gym"], true),
    m('warehouse_gym', 'Warehouse Gym', 'fitness', 'gym', ['warehouse\\s*gym'], true),
    // ── Travel ─────────────────────────────────────────────────────────────────
    m('emirates', 'Emirates Airlines', 'travel', 'flights', ['emirates\\s*airline', '\\bek\\s*ticket', 'emirates\\.com']),
    m('flydubai', 'flydubai', 'travel', 'flights', ['fly\\s*dubai', 'flydubai']),
    m('etihad', 'Etihad Airways', 'travel', 'flights', ['etihad']),
    m('air_arabia', 'Air Arabia', 'travel', 'flights', ['air\\s*arabia']),
    m('booking_com', 'Booking.com', 'travel', 'hotels', ['booking\\.com', 'bookingcom']),
    m('airbnb', 'Airbnb', 'travel', 'hotels', ['airbnb']),
    m('agoda', 'Agoda', 'travel', 'hotels', ['agoda']),
    // ── Government ─────────────────────────────────────────────────────────────
    m('dubai_police', 'Dubai Police', 'government', 'fines', ['dubai\\s*police', 'traffic\\s*fine']),
    m('gdrfa', 'GDRFA', 'government', 'visa', ['gdrfa', 'residency\\s*and\\s*foreigners']),
    m('amer', 'Amer Centre', 'government', 'visa', ['\\bamer\\b']),
    m('dubai_municipality', 'Dubai Municipality', 'government', 'fees', ['dubai\\s*municipality']),
    m('mohre', 'MOHRE', 'government', 'fees', ['mohre', 'ministry\\s*of\\s*human\\s*resources']),
    // ── Entertainment ──────────────────────────────────────────────────────────
    m('vox_cinemas', 'VOX Cinemas', 'entertainment', 'cinema', ['vox\\s*cinema', '\\bvox\\b']),
    m('reel_cinemas', 'Reel Cinemas', 'entertainment', 'cinema', ['reel\\s*cinema']),
    m('roxy', 'Roxy Cinemas', 'entertainment', 'cinema', ['roxy\\s*cinema']),
    m('platinumlist', 'Platinumlist', 'entertainment', 'events', ['platinumlist', 'platinum\\s*list']),
    m('steam', 'Steam', 'entertainment', 'gaming', ['steam\\s*games', 'steampowered', 'valve\\s*corp']),
    m('playstation', 'PlayStation', 'entertainment', 'gaming', ['playstation', 'sony\\s*interactive']),
    // ── Financial / transfers ──────────────────────────────────────────────────
    m('wise', 'Wise', 'transfers', 'remittance', ['\\bwise\\b', 'transferwise']),
    m('western_union', 'Western Union', 'transfers', 'remittance', ['western\\s*union']),
    m('al_ansari', 'Al Ansari Exchange', 'transfers', 'remittance', ['al\\s*ansari']),
    m('lulu_exchange', 'Lulu Exchange', 'transfers', 'remittance', ['lulu\\s*exchange']),
    m('uae_exchange', 'UAE Exchange', 'transfers', 'remittance', ['uae\\s*exchange']),
];
function m(normalizedName, displayName, defaultCategory, defaultSubcategory, patterns, subscriptionLikely = false) {
    return {
        normalizedName,
        displayName,
        defaultCategory,
        defaultSubcategory,
        patterns,
        countries: ['AE'],
        subscriptionLikely,
    };
}
export function compileMerchantPatterns(records = SEED_MERCHANTS) {
    const compiled = [];
    for (const merchant of records) {
        for (const p of merchant.patterns) {
            try {
                compiled.push({ merchant, regex: new RegExp(p, 'i'), specificity: p.length });
            }
            catch {
                // A malformed pattern must never take down ingestion; fall back to literal.
                compiled.push({
                    merchant,
                    regex: new RegExp(p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'),
                    specificity: p.length,
                });
            }
        }
    }
    return compiled.sort((a, b) => b.specificity - a.specificity);
}
//# sourceMappingURL=registry.js.map